/**
 * Browser for the site-configuration store.
 *
 * A SiteConfig anchors the tree; underneath, its page-content blocks (grouped
 * by type) are reachable as their own rows. Header nav and footer are no
 * longer separate entity kinds — they're embedded directly on SiteConfig
 * (a recursive menu tree and a single footer object, matching the real
 * runtime shape — see siteconfig/types.ts's fidelity note 1b) — so they show
 * as summary rows that select the SiteConfig itself and open its editor,
 * where the actual menu/footer editing happens.
 *
 * countries is passed in rather than read from this module's own store,
 * since country identity lives in the Services backend (services/types.ts's
 * Country entity) and siteconfig only holds countryId FKs — see
 * siteconfig/types.ts's fidelity notes. A SiteConfig can span several
 * countries at once, so each row's label lists all of them.
 */

import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import {
  ChevronRight, ChevronDown, Plus, Trash2, Search, RefreshCw,
  Globe, FileText, Link2, PanelBottom,
  Info, MapPin, Image as ImageIcon, Type, LayoutGrid, Rows3,
  Mail, MessageCircle, Megaphone, Target, BookOpen,
} from "lucide-react"
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { TreeRow } from "../shared/components/TreeRow"
import { coverageOf, describeLanguage, translationStatus, STATUS_LABEL, type LanguageInfo } from "../shared/languages"
import { CoverageLine } from "../shared/components/languages/CoverageLine"
import * as db from "./store"
import {
  type SiteConfigStore, type SiteConfigEntityKind, type PageContentType,
  siteConfigDisplayName, PAGECONTENT_TYPE_LABELS,
} from "./types"
import type { SiteConfigView } from "./useSiteConfigCMS"

/**
 * One icon per block type, so a content list is scannable without reading every
 * label. Every type has an entry — falling back to a generic file icon for most
 * of them made the list look uniform when the type is the most useful thing to
 * see at a glance.
 */
const TYPE_ICONS: Record<PageContentType, React.ComponentType<{ className?: string }>> = {
  about: BookOpen,
  mission: Target,
  channels: MessageCircle,
  info: Info,
  services: MapPin,
  image: ImageIcon,
  richtext: FileText,
  categories: LayoutGrid,
  sections: Rows3,
  Contact: Mail,
  popup: Megaphone,
  text: Type,
}

/** The icon for a block type, at the tree's standard size. */
function typeIcon(type: PageContentType): React.ReactNode {
  const Icon = TYPE_ICONS[type] ?? FileText
  return <Icon className="h-3.5 w-3.5" />
}

/**
 * A tiny thumbnail for the three block types that carry an image, so the tree
 * shows what a block actually holds instead of an identical file icon on every
 * row. Falls back to the generic icon when there is no image or it fails to
 * load — the editor is where a broken URL gets explained, not here.
 */
function blockThumb(block: any): React.ReactNode {
  const src = block.aboutImage || block.missionImage || block.image
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      className="h-3.5 w-3.5 shrink-0 rounded-sm object-cover"
      onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none" }}
    />
  )
}

/** Every block type, in the order the picker lists them. */
const PAGECONTENT_TYPES = Object.keys(PAGECONTENT_TYPE_LABELS) as PageContentType[]

export interface SiteConfigBrowserProps {
  store: SiteConfigStore
  locale: string
  language?: LanguageInfo
  countries: Array<{ id: string; name: string }>
  selected: { kind: SiteConfigEntityKind; id: string; view?: SiteConfigView } | null
  onSelect: (kind: SiteConfigEntityKind, id: string, view?: SiteConfigView) => void
  /** Returns the new entity's id, so a page-content block can be linked onto its site right after creation. */
  onCreate: (kind: SiteConfigEntityKind, fields: Record<string, any>) => string
  onUpdate: (kind: SiteConfigEntityKind, id: string, fields: Record<string, any>) => void
  onDelete: (kind: SiteConfigEntityKind, id: string) => void
}

export function SiteConfigBrowser({
  store, locale, language = describeLanguage(locale), countries, selected, onSelect, onCreate, onUpdate, onDelete,
}: SiteConfigBrowserProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [query, setQuery] = useState("")

  const toggle = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  const isSel = (kind: SiteConfigEntityKind, id: string) => selected?.kind === kind && selected?.id === id
  const countryName = (id: string) => countries.find(c => c.id === id)?.name ?? "(unknown country)"

  // Page blocks in the working language, for the footer
  const coverage = useMemo(
    () => coverageOf(Object.values(store.pageContents) as any[], locale, store.defaultLocale),
    [store, locale],
  )

  const hits = useMemo(() => {
    if (query.trim().length < 2) return null
    return db.searchStore(store, query, { limit: 60 })
  }, [store, query])


  /**
   * Moves a block within its config's contentIds by `delta`.
   *
   * contentIds order is the page render order, so this is the only thing that
   * decides where a block appears. Swapping two ids keeps the array a pure
   * permutation — no block can be dropped or duplicated by a reorder.
   */
  const moveBlock = (sc: any, index: number, delta: number) => {
    const ids = [...(sc.contentIds ?? [])]
    const target = index + delta
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    onUpdate("site", sc.id, { contentIds: ids })
  }

  const sitesView = () => {
    const configs = db.listEntities(store, "site")
    if (!configs.length) {
      return (
        <div className="p-4 text-center">
          <p className="mb-2 text-sm text-muted-foreground">No sites yet.</p>
          <Button
            variant="outline" size="sm"
            onClick={() => onCreate("site", { name: "New site" })}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Add site
          </Button>
        </div>
      )
    }

    return configs.map((sc: any) => {
      const open = expanded[`sc:${sc.id}`]
      const blocks = db.pageContentsOfSite(store, sc.id)
      const headerItemCount = db.flattenMenu(sc.headerMenu ?? []).length
      const footerLinkCount = sc.footer?.footerLinks?.length ?? 0

      return (
        <div key={sc.id}>
          <TreeRow
            kind="site" id={sc.id} depth={0}
            selected={isSel("site", sc.id) && (selected?.view ?? "settings") === "settings"}
            onSelect={() => onSelect("site", sc.id, "settings")}
            onDelete={onDelete}
            label={sc.name || "(untitled site)"}
            icon={<Globe className="h-3.5 w-3.5" />}
            expandable isOpen={open} onToggle={() => toggle(`sc:${sc.id}`)}
            badge={
              <span
                className="shrink-0 text-[10px] text-muted-foreground"
                // Geography is a scope, not identity, so it reads as a summary
                // here rather than as part of the site's name.
                title={(sc.serviceMap?.countryIds ?? []).map(countryName).join(", ") || "No geography scoped"}
              >
                {blocks.length}
              </span>
            }
          />
          {open && (
            <>
              {/*
                Header nav and footer are embedded on the config rather than
                being their own entities, but they are edited on their own
                screens — so they get real selectable rows, distinguished by
                the selection's `view` rather than by an entity kind.
              */}
              <TreeRow
                kind="site" id={sc.id} depth={1}
                selected={isSel("site", sc.id) && selected?.view === "headerNav"}
                onSelect={() => onSelect("site", sc.id, "headerNav")}
                onDelete={() => {}}
                deletable={false}
                label="Header nav"
                icon={<Link2 className="h-3.5 w-3.5" />}
                badge={<span className="shrink-0 text-[10px] text-muted-foreground">{headerItemCount}</span>}
              />

              <TreeRow
                kind="site" id={sc.id} depth={1}
                selected={isSel("site", sc.id) && selected?.view === "footer"}
                onSelect={() => onSelect("site", sc.id, "footer")}
                onDelete={() => {}}
                deletable={false}
                label="Footer"
                icon={<PanelBottom className="h-3.5 w-3.5" />}
                badge={<span className="shrink-0 text-[10px] text-muted-foreground">{footerLinkCount} link{footerLinkCount === 1 ? "" : "s"}</span>}
              />

              {/*
                Blocks are listed in contentIds order, NOT grouped by type.
                That order IS the order they render on the page, so grouping
                hid the one property most worth seeing — and made reordering
                impossible to reason about. Each row carries move controls that
                rewrite contentIds.
              */}
              <div style={{ paddingLeft: 16 }} className="flex items-center gap-1 py-1 text-xs">
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="font-medium text-muted-foreground">Content</span>
                <span className="text-[10px] text-muted-foreground">{blocks.length}</span>
                <span className="flex-1" />
                <span className="text-[10px] text-muted-foreground">in page order</span>
              </div>
              {blocks.map((b: any, i: number) => (
                <TreeRow
                  key={b.id} kind="pageContent" id={b.id} depth={2}
                  selected={isSel("pageContent", b.id)} onSelect={onSelect} onDelete={onDelete}
                  // Type FIRST: labels run long (a whole mission statement is a
                  // valid title), and a trailing type is the part that gets
                  // truncated away — losing the one token that says what the
                  // block actually is. Dimmed so it reads as a prefix rather
                  // than part of the title.
                  label={
                    <>
                      <span className="text-muted-foreground">{b.type}</span>
                      {" · "}
                      {siteConfigDisplayName("pageContent", b, locale, store.defaultLocale)}
                    </>
                  }
                  labelText={`${b.type} · ${siteConfigDisplayName("pageContent", b, locale, store.defaultLocale)}`}
                  icon={blockThumb(b) ?? typeIcon(b.type)}
                  status={translationStatus(b.translations, locale, store.defaultLocale)}
                  statusTitle={`${language.name}: ${STATUS_LABEL[translationStatus(b.translations, locale, store.defaultLocale)]}`}
                  onMoveUp={i > 0 ? () => moveBlock(sc, i, -1) : undefined}
                  onMoveDown={i < blocks.length - 1 ? () => moveBlock(sc, i, 1) : undefined}
                />
              ))}

              {/*
                A picker rather than a plain button: there are 12 block types and
                the agent can create any of them, so hardcoding one here left the
                other 11 reachable only by asking the agent.
              */}
              <div style={{ paddingLeft: 12 }} className="py-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 text-[11px]">
                      <Plus className="mr-1 h-3 w-3" /> Add page content block
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                    <DropdownMenuLabel className="text-[11px]">Block type</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {PAGECONTENT_TYPES.map(t => (
                      <DropdownMenuItem
                        key={t}
                        className="gap-2 text-xs"
                        onSelect={() => {
                          const id = onCreate("pageContent", { type: t })
                          onUpdate("site", sc.id, { contentIds: [...sc.contentIds, id] })
                        }}
                      >
                        {typeIcon(t)}
                        {PAGECONTENT_TYPE_LABELS[t]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          )}
        </div>
      )
    })
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1 border-b p-2">
        <div className="relative min-w-[140px] flex-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search sites, nav, page content…"
            className="h-8 pl-7 text-sm"
          />
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
        <div className="p-1">
          {hits ? (
            hits.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">No matches for "{query}".</p>
            ) : (
              hits.map(h => (
                <div
                  key={`${h.kind}:${h.id}`}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm",
                    isSel(h.kind, h.id) ? "bg-primary/10" : "hover:bg-muted/60",
                  )}
                  onClick={() => onSelect(h.kind, h.id)}
                >
                  <span className="truncate flex-1">{h.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px] px-1 py-0">{h.kind}</Badge>
                </div>
              ))
            )
          ) : (
            sitesView()
          )}
        </div>
      </ScrollArea>

      <div className="border-t p-2 text-[11px] text-muted-foreground">
        {(() => {
          const s = db.stats(store)
          return `${s.sites} site${s.sites === 1 ? "" : "s"} · ${s.pageContents} blocks · ${s.headerMenuItems} header nav items · ${s.footerLinks} footer links`
        })()}
      </div>
      <CoverageLine language={language} isSource={locale === store.defaultLocale} coverage={coverage} noun="page blocks" />
    </div>
  )
}
